import React from 'react';
import { ArrowLeft, Banknote, CheckCircle, CreditCard, DollarSign, Smartphone, UserCircle, UsersRound } from 'lucide-react';
import WaiterSelector from '../../WaiterSelector';

const TableCloseOrderModal = ({ closeOrderForm, currentOrder, handleCloseOrder, restaurant, selectedTable, setActiveSection, setCloseOrderForm, setIsCloseOrderModalOpen }) => {
  return (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 z-[10000] flex flex-col" data-testid="close-order-modal">
          {/* Header com botão de voltar */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 sm:px-6 py-4 shadow-xl flex-shrink-0">
            <div className="flex items-center gap-4 max-w-4xl mx-auto">
              <button
                onClick={() => setIsCloseOrderModalOpen(false)}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all flex-shrink-0"
                data-testid="close-modal-button"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Finalizar Pedido</h2>
                <p className="text-green-100 text-sm">{selectedTable?.name}</p>
              </div>
            </div>
          </div>
          
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="max-w-2xl mx-auto space-y-6">

              {/* Total Summary */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-gray-300 text-sm">Total a pagar</p>
                    <p className="text-white text-3xl sm:text-4xl font-bold mt-1">
                      R$ {((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1)).toFixed(2)}
                    </p>
                    {closeOrderForm.applyServiceFee && (
                      <p className="text-green-400 text-xs mt-1">Incluindo 10% de serviço</p>
                    )}
                  </div>
                  <div className="text-left sm:text-right">
                    {closeOrderForm.splitPeople > 1 && (
                      <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg px-4 py-3">
                        <p className="text-blue-300 text-xs">Por pessoa</p>
                        <p className="text-blue-200 text-xl font-bold">
                          R$ {(((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1)) / closeOrderForm.splitPeople).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-4 block flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Forma de Pagamento
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'dinheiro' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'dinheiro'
                        ? 'bg-green-500/20 border-green-500 shadow-lg shadow-green-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-cash-button"
                  >
                    <Banknote className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'dinheiro' ? 'text-green-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'dinheiro' ? 'text-white' : 'text-gray-400'}`}>Dinheiro</p>
                  </button>
                  
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'cartao' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'cartao'
                        ? 'bg-blue-500/20 border-blue-500 shadow-lg shadow-blue-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-card-button"
                  >
                    <CreditCard className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'cartao' ? 'text-blue-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'cartao' ? 'text-white' : 'text-gray-400'}`}>Cartão</p>
                  </button>
                  
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'pix' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'pix'
                        ? 'bg-purple-500/20 border-purple-500 shadow-lg shadow-purple-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-pix-button"
                  >
                    <Smartphone className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'pix' ? 'text-purple-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'pix' ? 'text-white' : 'text-gray-400'}`}>PIX</p>
                  </button>
                </div>

                {/* Cash Change Calculator - Appears only when cash is selected */}
                {closeOrderForm.paymentMethod === 'dinheiro' && (
                  <div className="mt-4 space-y-3 animate-in slide-in-from-top duration-300">
                    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-600/30 rounded-lg p-4">
                      <label className="text-green-300 text-sm font-medium mb-2 block flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Valor recebido do cliente
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400 font-bold text-lg">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={closeOrderForm.cashReceived}
                          onChange={(e) => setCloseOrderForm({ ...closeOrderForm, cashReceived: e.target.value })}
                          placeholder="0,00"
                          className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-green-600/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-lg font-semibold"
                          data-testid="cash-received-input"
                        />
                      </div>
                    </div>
                    
                    {closeOrderForm.cashReceived && parseFloat(closeOrderForm.cashReceived) > 0 && (
                      <div className={`p-4 rounded-lg border-2 transition-all ${
                        parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                          ? 'bg-green-500/10 border-green-500/40'
                          : 'bg-red-500/10 border-red-500/40'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${
                            parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? 'text-green-300'
                              : 'text-red-300'
                          }`}>
                            {parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? '💵 Troco a devolver'
                              : '⚠️ Valor insuficiente'
                            }
                          </span>
                          <span className={`text-2xl font-bold ${
                            parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? 'text-green-400'
                              : 'text-red-400'
                          }`}>
                            R$ {(parseFloat(closeOrderForm.cashReceived) - ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Split Bill */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-4 block flex items-center gap-2">
                  <UsersRound className="w-5 h-5 text-blue-400" />
                  Dividir Conta
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, splitPeople: Math.max(1, closeOrderForm.splitPeople - 1) })}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center transition-colors text-white font-bold text-xl shadow-lg"
                    data-testid="split-decrease-button"
                  >
                    −
                  </button>
                  <div className="flex-1 bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-4 text-center">
                    <p className="text-white text-3xl font-bold">{closeOrderForm.splitPeople}</p>
                    <p className="text-gray-400 text-sm mt-1">{closeOrderForm.splitPeople === 1 ? 'pessoa' : 'pessoas'}</p>
                  </div>
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, splitPeople: closeOrderForm.splitPeople + 1 })}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center transition-colors text-white font-bold text-xl shadow-lg"
                    data-testid="split-increase-button"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Service Fee Checkbox */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden">
                <div 
                  onClick={() => setCloseOrderForm({ ...closeOrderForm, applyServiceFee: !closeOrderForm.applyServiceFee })}
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  data-testid="service-fee-toggle"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all shadow-lg ${
                      closeOrderForm.applyServiceFee 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-gray-600'
                    }`}>
                      {closeOrderForm.applyServiceFee && (
                        <CheckCircle className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-base">Taxa de serviço</p>
                      <p className="text-gray-400 text-sm">Adicionar 10% ao total</p>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                    closeOrderForm.applyServiceFee 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {closeOrderForm.applyServiceFee ? 'SIM' : 'NÃO'}
                  </div>
                </div>
              </div>

              {/* Waiter Selector (dropdown com equipe) */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-3 block flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-orange-400" />
                  Atendente da mesa <span className="text-gray-500 text-sm">(opcional)</span>
                </label>
                <WaiterSelector
                  restaurantId={restaurant?.id}
                  value={closeOrderForm.waiterId || closeOrderForm.waiterName ? { id: closeOrderForm.waiterId, name: closeOrderForm.waiterName } : null}
                  onChange={(w) =>
                    setCloseOrderForm({
                      ...closeOrderForm,
                      waiterId: w?.id || null,
                      waiterName: w?.name || ''
                    })
                  }
                  onCreateTeamMember={() => {
                    setIsCloseOrderModalOpen(false);
                    setActiveSection && setActiveSection('team');
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at Bottom */}
          <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 px-4 sm:px-6 py-4 shadow-2xl">
            <div className="flex gap-3 max-w-2xl mx-auto">
              <button
                onClick={() => setIsCloseOrderModalOpen(false)}
                className="flex-1 px-6 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                data-testid="cancel-close-order-button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-base">Voltar</span>
              </button>
              <button
                onClick={handleCloseOrder}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all font-bold shadow-xl shadow-green-500/40 flex items-center justify-center gap-2"
                data-testid="confirm-close-order-button"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-base">Confirmar Fechamento</span>
              </button>
            </div>
          </div>
        </div>
  );
};

export default TableCloseOrderModal;
